import { useCallback, useEffect, useRef, useState } from "react";

import type { ScrollHashItem } from "../types";
import { smoothScrollTo } from "../utils/scrollUtils";
import {
  collectSections,
  findMatchingItem,
  pickActiveSection,
  shouldScheduleScrollDetection,
} from "./scrollHashTabLogic";

function useScrollHashTab(items: ScrollHashItem[] = [], idPrefix = "") {
  const [activeTabId, setActiveTabId] = useState<number | string | null>(null);
  const scrollSpyEnabledRef = useRef(false);

  useEffect(() => {
    if (window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  const updateHash = useCallback((newHash: string) => {
    const nextHash = `#${newHash}`;
    if (window.location.hash === nextHash) {
      return;
    }
    window.history.pushState(null, "", nextHash);
  }, []);

  const handleScroll = useCallback(
    (targetId: string) => {
      const section = document.getElementById(targetId);
      if (section) {
        const sectionTop =
          section.getBoundingClientRect().top + window.pageYOffset;

        const isMobile = window.innerWidth < 768;
        const offset = isMobile ? 36 : 72;

        scrollSpyEnabledRef.current = false;
        smoothScrollTo(sectionTop - offset, 300, () => {
          setTimeout(() => {
            scrollSpyEnabledRef.current = true;
          }, 100);
        });

        setTimeout(() => {
          updateHash(targetId);
        }, 50);
      }
    },
    [updateHash],
  );

  const handleSetActiveTabId = useCallback(
    (id: number | string) => {
      setActiveTabId(id);
      const targetId = idPrefix ? `${idPrefix}-${id}` : String(id);
      handleScroll(targetId);
    },
    [handleScroll, idPrefix],
  );

  useEffect(() => {
    if (!items.length) return;

    let ticking = false;

    const detectActiveSection = () => {
      if (!scrollSpyEnabledRef.current) return;

      if (shouldScheduleScrollDetection(ticking)) {
        requestAnimationFrame(() => {
          const sections = collectSections(items, idPrefix);
          const activeSection = pickActiveSection(
            sections,
            window.scrollY,
            window.innerHeight,
          );

          /* istanbul ignore if */
          if (!activeSection) return;

          const currentHash = window.location.hash.substring(1);
          /* istanbul ignore if */
          if (currentHash === activeSection.targetId) return;

          updateHash(activeSection.targetId);
          setActiveTabId(activeSection.id);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", detectActiveSection, { passive: true });

    return () => {
      window.removeEventListener("scroll", detectActiveSection);
    };
  }, [items, idPrefix, updateHash]);

  useEffect(() => {
    if (!items.length) return;

    const hash = window.location.hash;
    if (hash) {
      const param = hash.substring(1);
      const scrollToHash = () => {
        const element = document.getElementById(param);
        if (element) {
          const matchingItem = findMatchingItem(items, param, idPrefix);

          if (matchingItem) {
            setActiveTabId(matchingItem.id);
          }

          handleScroll(param);
          setTimeout(() => {
            scrollSpyEnabledRef.current = true;
          }, 500);
        } else {
          setTimeout(scrollToHash, 50);
        }
      };

      setTimeout(scrollToHash, 150);
      return;
    }

    scrollSpyEnabledRef.current = true;
  }, [items, handleScroll, idPrefix, updateHash]);

  return {
    handleScroll,
    handleSetActiveTabId,
    updateHash,
    activeTabId,
  };
}

export default useScrollHashTab;
